import React, { useEffect, useState } from "react";
import { api, downloadCsv } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import { SalesStats } from "../components/SalesStats";
import { Payouts } from "../components/Payouts";
import { AdminNav } from "../components/AdminNav";
import { Video, Download, Trophy } from "lucide-react";

export default function Admin() {
  const [pin, setPin] = useState(localStorage.getItem("host_pin") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("host_pin"));
  const [games, setGames] = useState([]);
  const [testName, setTestName] = useState("Tester");
  const [testTickets, setTestTickets] = useState([]);
  const [streamEdit, setStreamEdit] = useState({});
  const [form, setForm] = useState({ title: "Bingo Avond", ticket_price: 2.5, prize_row: 50, prize_two_rows: 100, prize_full: 500, stream_url: "" });

  const saveStream = async (g) => {
    try {
      await api.post(`/games/${g.id}/stream`, { stream_url: streamEdit[g.id] ?? g.stream_url ?? "", pin });
      toast.success("Livestream-link opgeslagen");
      loadGames();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij opslaan");
    }
  };

  const activeGame = games.find((g) => ["lobby", "live", "paused"].includes(g.status));

  const makeTestTicket = async () => {
    if (!activeGame) { toast.error("Geen actief spel"); return; }
    if (!testName.trim()) { toast.error("Vul een naam in"); return; }
    try {
      const r = await api.post("/tickets/free", { game_id: activeGame.id, display_name: testName.trim(), pin });
      const existing = JSON.parse(localStorage.getItem("my_tickets") || "[]");
      localStorage.setItem("my_tickets", JSON.stringify([...existing, r.data.id]));
      setTestTickets((t) => [r.data, ...t]);
      toast.success("Testkaart aangemaakt!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij aanmaken testkaart");
    }
  };

  const loadGames = async () => {
    const r = await api.get("/games");
    setGames(r.data);
  };
  useEffect(() => { if (authed) loadGames(); }, [authed]);

  const submitPin = async () => {
    if (!pin) return;
    try {
      await api.post("/auth/pin", { pin });
      localStorage.setItem("host_pin", pin);
      setAuthed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ongeldige PIN");
    }
  };

  const create = async () => {
    try {
      await api.post("/games", { ...form, pin });
      toast.success("Spel aangemaakt!");
      setForm({ ...form, title: "Bingo Avond " + new Date().toLocaleDateString("nl-NL") });
      loadGames();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij aanmaken");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-6">
        <Toaster theme="dark" position="top-center" richColors />
        <div className="w-full max-w-sm p-8 rounded-2xl bg-[#12141D] border border-white/10">
          <h1 className="text-2xl font-black text-slate-100 mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>Admin Login</h1>
          <p className="text-sm text-slate-400 mb-6">Voer de admin PIN in.</p>
          <Input data-testid="admin-pin" type="password" value={pin} onChange={(e) => setPin(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && submitPin()} className="bg-[#0B0C10] border-white/10 text-white" />
          <Button data-testid="admin-pin-submit" onClick={submitPin} className="mt-4 w-full bg-[#FFD700] text-[#0B0C10] hover:bg-[#E6C200]">Inloggen</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] text-slate-100 p-6">
      <Toaster theme="dark" position="top-center" richColors />
      <div className="max-w-4xl mx-auto space-y-8">
        <AdminNav />
        <header>
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Admin</div>
          <h1 className="text-4xl font-black" style={{ fontFamily: "Unbounded, sans-serif" }}>Nieuwe Bingo Ronde</h1>
        </header>

        <div className="p-6 rounded-2xl bg-[#12141D] border border-white/10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Titel</Label>
            <Input data-testid="admin-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Prijs per kaart (€)</Label>
            <Input data-testid="admin-price" type="number" step="0.5" value={form.ticket_price}
                   onChange={(e) => setForm({ ...form, ticket_price: parseFloat(e.target.value) })}
                   className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Prijs 1 rij (€)</Label>
            <Input data-testid="admin-p1" type="number" value={form.prize_row}
                   onChange={(e) => setForm({ ...form, prize_row: parseFloat(e.target.value) })}
                   className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Prijs 2 rijen (€)</Label>
            <Input data-testid="admin-p2" type="number" value={form.prize_two_rows}
                   onChange={(e) => setForm({ ...form, prize_two_rows: parseFloat(e.target.value) })}
                   className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-400">Prijs volle kaart (€)</Label>
            <Input data-testid="admin-p3" type="number" value={form.prize_full}
                   onChange={(e) => setForm({ ...form, prize_full: parseFloat(e.target.value) })}
                   className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1"><Video className="w-3 h-3" /> Livestream-link (optioneel — YouTube, Vimeo of Twitch)</Label>
            <Input data-testid="admin-stream-url" value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })}
                   placeholder="https://www.youtube.com/watch?v=..." className="mt-1 bg-[#0B0C10] border-white/10 text-white" />
            <p className="text-xs text-slate-500 mt-1">Kijkers zien deze video boven hun bingokaart. Je kunt de link later nog aanpassen.</p>
          </div>
          <div className="md:col-span-2">
            <Button data-testid="admin-create" onClick={create}
                    className="w-full h-12 bg-gradient-to-r from-[#FFD700] to-[#FF007F] text-[#0B0C10] font-black text-lg">
              Maak spel aan
            </Button>
          </div>
        </div>

        <SalesStats pin={pin} refreshKey={games.length + testTickets.length} />

        <Payouts pin={pin} refreshKey={games.length} />

        <div className="p-6 rounded-2xl bg-[#12141D] border border-[#00F0FF]/30" data-testid="test-ticket-section">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00F0FF]">Testen zonder betaling</div>
          <h2 className="text-2xl font-bold mt-1" style={{ fontFamily: "Unbounded, sans-serif" }}>Gratis testkaart</h2>
          <p className="text-sm text-slate-400 mt-2">
            Maakt direct een betaalde kaart aan voor het actieve spel{activeGame ? <> (<b className="text-slate-200">{activeGame.title}</b>)</> : ""}, zonder Stripe. Open de link op je telefoon om mee te spelen.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Input data-testid="test-ticket-name" value={testName} onChange={(e) => setTestName(e.target.value)} placeholder="Naam speler"
                   className="bg-[#0B0C10] border-white/10 text-white" />
            <Button data-testid="test-ticket-create" onClick={makeTestTicket} disabled={!activeGame}
                    className="h-10 px-6 bg-[#00F0FF] text-[#0B0C10] font-black hover:bg-[#00d4e0] disabled:opacity-40">
              Maak testkaart
            </Button>
          </div>
          {!activeGame && <p className="text-xs text-[#FFD700] mt-2">Maak eerst hieronder een spel aan.</p>}
          {testTickets.length > 0 && (
            <div className="mt-4 space-y-2">
              {testTickets.map((t) => (
                <div key={t.id} data-testid={`test-ticket-${t.id}`} className="p-3 rounded-lg bg-[#0B0C10] border border-white/10 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-bold text-sm">{t.display_name}</div>
                    <div className="font-mono text-xs text-slate-500 break-all">{window.location.origin}/viewer/play/{t.id}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`test-ticket-copy-${t.id}`} variant="outline" size="sm" className="border-white/10 bg-[#12141D] text-slate-100 hover:bg-[#1F2833]"
                            onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/viewer/play/${t.id}`); toast.success("Link gekopieerd"); }}>
                      Kopieer link
                    </Button>
                    <a data-testid={`test-ticket-open-${t.id}`} href={`/viewer/play/${t.id}`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center px-3 rounded-md text-sm font-bold bg-[#FFD700] text-[#0B0C10] hover:bg-[#E6C200]">
                      Open kaart →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-2xl font-bold" style={{ fontFamily: "Unbounded, sans-serif" }}>Alle spellen</h2>
          </div>
          <div className="space-y-2">
            {games.map((g) => (
              <div key={g.id} data-testid={`game-row-${g.id}`} className="p-4 rounded-xl bg-[#12141D] border border-white/10 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold">{g.title}</div>
                    <div className="text-xs text-slate-500">€{g.ticket_price} · {new Date(g.created_at).toLocaleString("nl-NL")}</div>
                  </div>
                  <div className={`px-3 py-1 rounded text-xs font-bold ${
                    g.status === "live" ? "bg-[#00E676]/20 text-[#00E676]" :
                    g.status === "finished" ? "bg-white/10 text-slate-400" :
                    "bg-[#FFD700]/20 text-[#FFD700]"
                  }`}>{g.status}</div>
                </div>
                {Object.values(g.winners || {}).some(Boolean) && (
                  <div className="flex flex-wrap items-center gap-3 text-xs" data-testid={`winners-${g.id}`}>
                    <span className="inline-flex items-center gap-1 text-slate-400"><Trophy className="w-3 h-3 text-[#FFD700]" /> Winnaars:</span>
                    {["row", "two_rows", "full"].map((s) => g.winners[s] && (
                      <span key={s} className="px-2 py-0.5 rounded bg-[#00E676]/15 text-[#00E676] font-bold">{g.winners[s].display_name} <span className="text-slate-400 font-normal">· {s === "row" ? "1 rij" : s === "two_rows" ? "2 rijen" : "volle kaart"}</span></span>
                    ))}
                    <button data-testid={`export-winners-${g.id}`} onClick={() => downloadCsv(`/games/${g.id}/winners.csv`, `winnaars-${g.title.replace(/[^\w-]/g, "_")}.csv`).catch(() => toast.error("Download mislukt"))}
                       className="inline-flex items-center gap-1 text-[#FFD700] hover:underline ml-auto"><Download className="w-3 h-3" /> Download CSV</button>
                  </div>
                )}
                {g.status !== "finished" && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid={`stream-input-${g.id}`} value={streamEdit[g.id] ?? g.stream_url ?? ""}
                           onChange={(e) => setStreamEdit({ ...streamEdit, [g.id]: e.target.value })}
                           placeholder="Livestream-link (YouTube / Vimeo / Twitch)" className="bg-[#0B0C10] border-white/10 text-white text-sm" />
                    <Button data-testid={`stream-save-${g.id}`} onClick={() => saveStream(g)} variant="outline" size="sm"
                            className="border-white/10 bg-[#0B0C10] text-slate-100 hover:bg-[#1F2833] h-10">
                      <Video className="w-4 h-4 mr-1" /> Opslaan
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {games.length === 0 && <div className="text-slate-500 text-sm">Nog geen spellen</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
